import type { BasketUpsellCategory, UpsellsContentBlock } from '@library/api-menu-experience'

import type { BasketUpsellProduct, LimitReached } from './types'

type BasketQuantities = Record<string, number>

type BuildUpsellsContentBlockViewModelInput = {
  basketGifts?: BasketQuantities
  basketProductQuantities: BasketQuantities
  block: UpsellsContentBlock
  pendingProductIds?: ReadonlySet<string>
}

type ProductLimit = {
  boxLimit: number
  categories: { id: string }[]
}

type ProductLimitMaps = {
  categories: Record<string, BasketUpsellCategory>
  products: Record<string, ProductLimit>
}

const getAllProductQuantities = (
  basketProductQuantities: BasketQuantities,
  basketGifts: BasketQuantities,
) =>
  Object.entries(basketProductQuantities).reduce<BasketQuantities>(
    (quantities, [productId, quantity]) => ({
      ...quantities,
      [productId]: quantity + (basketGifts[productId] ?? 0),
    }),
    basketGifts,
  )

const getProductLimitMaps = ({
  categories,
  maxProductsPerBox,
  products,
}: UpsellsContentBlock): ProductLimitMaps => {
  const categoriesByProductId = categories.reduce<Record<string, { id: string }[]>>(
    (productCategories, category) => {
      category.products.forEach(({ id }) => {
        productCategories[id] = [...(productCategories[id] ?? []), { id: category.id }]
      })

      return productCategories
    },
    {},
  )

  return {
    categories: categories.reduce<Record<string, BasketUpsellCategory>>(
      (categoryMap, category) => ({ ...categoryMap, [category.id]: category }),
      {},
    ),
    products: Object.values(products).reduce<Record<string, ProductLimit>>(
      (productMap, product) => ({
        ...productMap,
        [product.id]: {
          boxLimit: product.boxLimit ?? maxProductsPerBox,
          categories: categoriesByProductId[product.id] ?? [],
        },
      }),
      {},
    ),
  }
}

const getLimitReached = ({
  allProductQuantities,
  block,
  productLimitMaps,
  productId,
}: {
  allProductQuantities: BasketQuantities
  block: UpsellsContentBlock
  productLimitMaps: ProductLimitMaps
  productId: string
}): LimitReached => {
  const totalProductCount = Object.values(allProductQuantities).reduce(
    (total, quantity) => total + quantity,
    0,
  )

  if (totalProductCount >= block.maxProductsPerBox) {
    return { type: 'box', value: block.maxProductsPerBox }
  }

  const productLimit = productLimitMaps.products[productId]
  const productQuantity = allProductQuantities[productId] ?? 0
  if (productLimit && productQuantity >= productLimit.boxLimit) {
    return { type: 'item', value: productLimit.boxLimit }
  }

  const categoryAtLimit = productLimit?.categories.find(({ id: categoryId }) => {
    const category = productLimitMaps.categories[categoryId]
    if (!category) return false

    const categoryQuantity = Object.entries(allProductQuantities).reduce(
      (total, [basketProductId, quantity]) =>
        total +
        (productLimitMaps.products[basketProductId]?.categories.some(({ id }) => id === categoryId)
          ? quantity
          : 0),
      0,
    )

    return categoryQuantity >= (category.boxLimit ?? block.maxProductsPerBox)
  })

  if (categoryAtLimit) {
    return {
      type: 'category',
      value: productLimitMaps.categories[categoryAtLimit.id].title,
    }
  }

  return false
}

export const buildUpsellsContentBlockViewModel = ({
  basketGifts = {},
  basketProductQuantities,
  block,
  pendingProductIds = new Set<string>(),
}: BuildUpsellsContentBlockViewModelInput) => {
  const productLimitMaps = getProductLimitMaps(block)
  const allProductQuantities = getAllProductQuantities(basketProductQuantities, basketGifts)

  const toProductView = (productId: string): BasketUpsellProduct | undefined => {
    const product = block.products[productId]
    if (!product) return undefined

    const qty = basketProductQuantities[product.id] ?? 0
    const limitReached = getLimitReached({
      allProductQuantities,
      block,
      productLimitMaps,
      productId: product.id,
    })
    const outOfStock = product.stock - qty <= 0

    return {
      ...product,
      isAvailable: !outOfStock && !limitReached,
      isPending: pendingProductIds.has(product.id),
      limitReached,
      outOfStock,
      qty,
    }
  }

  const carouselProducts =
    block.carousel?.productIds
      .map(toProductView)
      .filter((product): product is BasketUpsellProduct => product !== undefined)
      .filter((product) => product.stock > 0) ?? []

  const selectedProducts = Object.keys(basketProductQuantities)
    .map(toProductView)
    .filter((product): product is BasketUpsellProduct => Boolean(product))

  return {
    carousel: block.carousel
      ? {
          tag: block.carousel.tag,
          title: block.carousel.title,
          subtitle: block.carousel.subtitle,
        }
      : undefined,
    carouselProducts,
    selectedProducts,
  }
}
